import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { X, Edit2, Building2 } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { Button } from '@/components/ui/Button'
import { Badge, getStatusVariant } from '@/components/ui/Badge'
import { EmpresaInfoTab } from '@/components/Empresas/Tabs/EmpresaInfoTab'
import { EmpresaContactsTab } from '@/components/Empresas/Tabs/EmpresaContactsTab'
import { EmpresaDealsTab } from '@/components/Empresas/Tabs/EmpresaDealsTab'
import { EmpresaHistoryTab } from '@/components/Empresas/Tabs/EmpresaHistoryTab'
import { EmpresaFinanceiroTab } from '@/components/Empresas/Tabs/EmpresaFinanceiroTab'
import RelationshipMap from '@/components/Empresas/RelationshipMap'
import api from '@/services/api'
import { cn } from '@/utils/cn'
import type { EmpresaDetailed, Partner } from '@/types'
import { toast } from 'sonner'
import { getFriendlyError } from '@/utils/getFriendlyError'
import { buildEmpresaUpdatePayload } from '@/utils/empresaCnpjLookup'
import { EMPRESA_STATUS_LABELS, EMPRESA_STATUS_LEGACY_LABELS } from '@/pages/empresasPageConstants'

interface EmpresaDetailModalProps {
  empresa: EmpresaDetailed | null
  onClose: () => void
  onSaved?: () => void
  partners?: Partner[]
}

const statusLabels: Record<string, string> = {
  ...EMPRESA_STATUS_LABELS,
  ...EMPRESA_STATUS_LEGACY_LABELS,
}

function readEmpresaPayload(body: unknown): EmpresaDetailed | null {
  if (!body || typeof body !== 'object') return null
  const record = body as { id?: string; data?: EmpresaDetailed }
  if (record.data?.id) return record.data
  if (record.id) return record as EmpresaDetailed
  return null
}

type TabId = 'info' | 'contacts' | 'deals' | 'history' | 'map' | 'financeiro'

const tabs: { id: TabId; label: string }[] = [
  { id: 'info', label: 'Informações' },
  { id: 'contacts', label: 'Contatos' },
  { id: 'deals', label: 'Deals' },
  { id: 'history', label: 'Histórico' },
  { id: 'map', label: 'Mapa de Poder' },
  { id: 'financeiro', label: 'Financeiro' },
]

export function EmpresaDetailModal({ empresa, onClose, onSaved, partners }: EmpresaDetailModalProps) {
  const [activeTab, setActiveTab] = useState<TabId>('info')
  const [editing, setEditing] = useState(false)
  const [editData, setEditData] = useState<Partial<EmpresaDetailed>>({})
  const [saving, setSaving] = useState(false)
  const [detailEmpresa, setDetailEmpresa] = useState<EmpresaDetailed | null>(null)

  useEffect(() => {
    if (empresa) {
      setEditData({ ...empresa })
      setEditing(false)
      setActiveTab('info')
      const controller = new AbortController()
      api.get(`/empresas/${empresa.id}`, { signal: controller.signal })
        .then((resp) => setDetailEmpresa(readEmpresaPayload(resp.data) ?? empresa))
        .catch(() => setDetailEmpresa(empresa))
      return () => controller.abort()
    }
    setDetailEmpresa(null)
  }, [empresa])

  const currentEmpresa = detailEmpresa ?? empresa

  const handleSave = async () => {
    if (!empresa) return
    setSaving(true)
    try {
      await api.patch(`/empresas/${empresa.id}`, buildEmpresaUpdatePayload(editData))
      const resp = await api.get(`/empresas/${empresa.id}`)
      setDetailEmpresa(readEmpresaPayload(resp.data) ?? empresa)
      setEditing(false)
      onSaved?.()
      toast.success('Empresa atualizada com sucesso')
    } catch (err: unknown) {
      toast.error(getFriendlyError(err, 'Erro ao salvar empresa'))
    } finally {
      setSaving(false)
    }
  }

  const refreshData = async () => {
    if (!empresa) return
    try {
      const resp = await api.get(`/empresas/${empresa.id}`)
      setDetailEmpresa(readEmpresaPayload(resp.data) ?? empresa)
      onSaved?.()
    } catch (err) {
      // Ignore
    }
  }

  if (!empresa || !currentEmpresa) return null

  return createPortal(
    <AnimatePresence>
      <div className='fixed inset-0 z-50 flex items-center justify-center p-4'>
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className='absolute inset-0 bg-black/60 backdrop-blur-sm'
          onClick={onClose}
        />

        {/* Dialog */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 8 }}
          transition={{ duration: 0.15, ease: 'easeOut' }}
          className='relative w-full max-w-4xl max-h-[90vh] z-10 bg-[var(--bg-card)] border border-[var(--glass-border)] rounded-xl shadow-2xl flex flex-col overflow-hidden'
        >
          {/* Header */}
          <div className='flex items-center justify-between p-5 border-b border-[var(--glass-border)]'>
            <div className='flex items-center gap-3'>
              <div className='w-10 h-10 rounded-lg bg-[var(--brand-primary-muted)] flex items-center justify-center'>
                <Building2 size={18} className='text-[var(--brand-primary)]' />
              </div>
              <div>
                <h2
                  className='font-semibold text-[var(--font-color-h2)]'
                  style={{ fontSize: 'var(--font-size-h3)' }}
                >
                  {currentEmpresa.nome ?? currentEmpresa.razaoSocial}
                </h2>
                <div className='flex items-center gap-2 mt-1'>
                  <Badge variant={getStatusVariant(currentEmpresa.status)}>
                    {statusLabels[currentEmpresa.status]}
                  </Badge>
                  {currentEmpresa.segmento && (
                    <span
                      className='text-[var(--font-color-muted)]'
                      style={{ fontSize: 'var(--font-size-xs)' }}
                    >
                      {currentEmpresa.segmento}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className='p-1.5 rounded-md text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--glass-bg)] transition-colors'
            >
              <X size={16} />
            </button>
          </div>

          {/* Tabs — wrap para nunca sumirem em telas estreitas */}
          <div className='flex flex-wrap gap-x-0 gap-y-0 border-b border-[var(--glass-border)] px-3 sm:px-5'>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'px-2.5 sm:px-4 py-2.5 sm:py-3 text-[12px] sm:text-[13px] font-medium transition-colors relative whitespace-nowrap',
                  activeTab === tab.id
                    ? 'text-[var(--brand-primary)]'
                    : 'text-[var(--text-muted)] hover:text-[var(--text-primary)]'
                )}
              >
                {tab.label}
                {activeTab === tab.id && (
                  <motion.div
                    layoutId='empresa-tab-indicator'
                    className='absolute bottom-0 left-0 right-0 h-0.5 bg-[var(--brand-primary)]'
                  />
                )}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className='min-h-0 flex-1 overflow-y-auto p-5'>
            {activeTab === 'info' && (
              <EmpresaInfoTab
                empresa={currentEmpresa}
                editing={editing}
                editData={editData}
                onEditDataChange={setEditData}
                partners={partners}
              />
            )}
            {activeTab === 'contacts' && (
              <EmpresaContactsTab
                empresa={currentEmpresa}
                onRefresh={refreshData}
              />
            )}
            {activeTab === 'deals' && (
              <EmpresaDealsTab empresa={currentEmpresa} />
            )}
            {activeTab === 'history' && (
              <EmpresaHistoryTab empresa={currentEmpresa} />
            )}
            {activeTab === 'map' && (
              <RelationshipMap
                contatos={currentEmpresa.contatos ?? []}
                companyId={currentEmpresa.id}
              />
            )}
            {activeTab === 'financeiro' && (
              <EmpresaFinanceiroTab
                empresa={currentEmpresa}
                editing={editing}
                editData={editData}
                onEditDataChange={setEditData}
              />
            )}
          </div>

          {/* Footer */}
          <div className='flex items-center justify-end gap-2 px-5 py-4 border-t border-[var(--glass-border)]'>
            {editing ? (
              <>
                <Button variant='ghost' size='sm' onClick={() => setEditing(false)}>
                  Cancelar
                </Button>
                <Button variant='primary' size='sm' loading={saving} onClick={handleSave}>
                  Salvar
                </Button>
              </>
            ) : (
              <>
                <Button variant='ghost' size='sm' onClick={onClose}>
                  Fechar
                </Button>
                {activeTab !== 'map' && activeTab !== 'deals' && activeTab !== 'history' && activeTab !== 'contacts' && (
                  <Button
                    variant='primary'
                    size='sm'
                    icon={<Edit2 size={13} />}
                    onClick={() => { setEditData({ ...currentEmpresa }); setEditing(true) }}
                  >
                    Editar
                  </Button>
                )}
              </>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>,
    document.body
  )
}
